import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../lib/apiClient';
import TurnstileWidget from './TurnstileWidget';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Public signup page — /signup.
 * Connects to A5 Sprint 6 backend: turnstile + register + funnel.
 */
export default function PublicSignupPage() {
  const { t } = useTranslation();

  // Read URL params for share-to-signup flow (A5-E4)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const fromShare = urlParams?.get('from_share') || null;
  const prefillEmail = urlParams?.get('prefill_email') || '';

  const [email, setEmail] = useState(prefillEmail);
  const [name, setName] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [siteKey, setSiteKey] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorCode, setErrorCode] = useState(null); // 400 | 403 | 409 | 5xx

  // Fetch turnstile site_key on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/v1/aif/turnstile/config');
        if (res?.data?.site_key) setSiteKey(res.data.site_key);
      } catch { /* Turnstile config fetch failed — form still usable without it */ }
    })();
  }, []);

  // Record funnel event on mount (fire-and-forget)
  useEffect(() => {
    apiClient.post('/api/v1/aif/funnel/event', {
      event_type: 'landing_viewed',
      metadata: {},
    }).catch(() => {});
  }, []);

  const emailValid = EMAIL_RE.test(email);
  const canSubmit = emailValid && turnstileToken && status !== 'submitting';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setErrorCode(null);

    try {
      await apiClient.post('/api/v1/aif/auth/register', {
        email,
        name: name || undefined,
        newsletter_opt_in: newsletterOptIn,
        turnstile_token: turnstileToken,
        language: 'de',
        ...(fromShare ? { from_share: fromShare } : {}),
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      const code = err?.status;
      if (code === 400 || code === 403 || code === 409) {
        setErrorCode(code);
      } else {
        setErrorCode(500);
      }
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-sm text-[var(--aif-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--aif-color-primary)]/30';

  if (status === 'success') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)] px-6">
        <div className="text-center space-y-4 max-w-sm" data-testid="signup-success">
          <div className="text-5xl">{'\u2709\uFE0F'}</div>
          <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">
            {t('signup.checkEmail', { defaultValue: 'Check your email' })}
          </h2>
          <p className="text-sm text-[var(--aif-text-secondary)]">
            {t('signup.magicLinkSent', { defaultValue: 'We sent you a magic link. Click it to complete your signup.' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-tertiary)] flex flex-col">
      {/* Header */}
      <div className="bg-[var(--aif-color-primary)] text-white px-6 py-5">
        <div className="text-lg font-bold flex items-center gap-2">
          <span>{'\uD83C\uDF3E'}</span> AI Farm
        </div>
        <p className="text-sm opacity-90 mt-1">
          {t('signup.headline', { defaultValue: 'Create your free account' })}
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-6 max-w-md mx-auto w-full">
        {fromShare && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800" data-testid="from-share-banner">
            {t('signup.fromShare', { defaultValue: 'Continuing from shared content' })}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('signup.emailPlaceholder', { defaultValue: 'E-Mail' })}
              required
              className={inputClass}
              data-testid="signup-email"
            />
          </div>
          <div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('signup.namePlaceholder', { defaultValue: 'Name (optional)' })}
              className={inputClass}
              data-testid="signup-name"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newsletterOptIn}
              onChange={(e) => setNewsletterOptIn(e.target.checked)}
              className="w-5 h-5 rounded border-[var(--aif-border-default)] accent-[var(--aif-color-primary)]"
              data-testid="signup-newsletter"
            />
            <span className="text-sm text-[var(--aif-text-secondary)]">
              {t('signup.newsletter', { defaultValue: 'Subscribe to our newsletter' })}
            </span>
          </label>

          {siteKey && (
            <TurnstileWidget
              siteKey={siteKey}
              onVerify={(token) => setTurnstileToken(token)}
            />
          )}

          {/* Error messages */}
          {status === 'error' && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="signup-error">
              {errorCode === 400 && t('signup.error400', { defaultValue: 'Invalid request. Please check your input.' })}
              {errorCode === 403 && t('signup.error403', { defaultValue: 'Registration is currently disabled.' })}
              {errorCode === 409 && t('signup.error409', { defaultValue: 'An account with this email already exists.' })}
              {errorCode === 500 && t('signup.error500', { defaultValue: 'Something went wrong. Please try again.' })}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 rounded-xl bg-[var(--aif-color-primary)] text-white font-medium text-sm disabled:opacity-50 transition"
            data-testid="signup-submit"
          >
            {status === 'submitting'
              ? t('signup.submitting', { defaultValue: 'Sending...' })
              : t('signup.submit', { defaultValue: 'Create account' })}
          </button>
        </form>

        {/* Legal links */}
        <div className="mt-6 text-center text-xs text-[var(--aif-text-muted)] space-x-3">
          <a href="/datenschutz" className="hover:underline">{t('legal.privacy', { defaultValue: 'Datenschutz' })}</a>
          <span>|</span>
          <a href="/agb" className="hover:underline">{t('legal.terms', { defaultValue: 'AGB' })}</a>
        </div>
      </div>
    </div>
  );
}
